"use client";

import Link from "next/link";
import Image from "next/image";
import { Product, Category } from "@prisma/client";
import { formatCurrency } from "@/lib/utils/general";

interface ProductCardProps {
	product: Product & {
		category: Category | null;
		isPopular: boolean;
		displayPrice?: number;
		isBulk?: boolean;
		bulkQuantity?: number;
	};
}

const ProductCard = ({ product }: ProductCardProps) => {
	const {
		name,
		shortDescription,
		price,
		images,
		category: productCategory,
		stock,
		hasDiscount,
		discountedPrice,
		slug,
		isPopular,
		displayPrice,
		isBulk,
		bulkQuantity,
		isInstantDelivery,
		instantDeliveryCodes,
		enableBulkPurchase,
		bulkPurchaseQuantities,
	} = product;
	const isOutOfStock =
		stock <= 0 ||
		(isInstantDelivery && instantDeliveryCodes
			? JSON.parse(JSON.stringify(instantDeliveryCodes)).filter(
					(code: { code: string; description?: string; isUsed: boolean }) =>
						!code.isUsed
				).length <
				(enableBulkPurchase && bulkPurchaseQuantities
					? JSON.parse(JSON.stringify(bulkPurchaseQuantities))[0]
					: 1)
			: false);

	// Calculate discount percentage
	const discountPercentage =
		hasDiscount && discountedPrice
			? Math.round(((price - discountedPrice) / price) * 100)
			: 0;

	return (
		<div
			className={`overflow-hidden transition-all duration-300 rounded-xl group ${
				isPopular
					? "bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 border-2 border-primary/40 hover:border-primary/60 shadow-lg hover:shadow-primary/30"
					: "bg-base-100 border border-base-300"
			} hover:shadow-xl flex flex-col h-full ${
				isOutOfStock ? "opacity-90" : ""
			}`}
		>
			<div className="relative flex flex-col h-full">
				<Link href={`/products/${slug}`} className="block flex-grow">
					<div className="relative w-full h-64">
						{images && images.length > 0 ? (
							<Image
								src={images[0]}
								alt={name}
								fill
								sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
								className={`object-cover transition-transform duration-300 ${
									!isOutOfStock ? "group-hover:scale-105" : ""
								}`}
								loading="lazy"
								placeholder="blur"
								blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9IiNlMmUyZTIiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1mYW1pbHk9InN5c3RlbS11aSI7IGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5YTlhOWEiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5Mb2FkaW5nLi4uPC90ZXh0Pjwvc3ZnPg=="
							/>
						) : (
							<div className="flex items-center justify-center w-full h-full bg-base-200">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									strokeWidth={1.5}
									stroke="currentColor"
									className="w-12 h-12 text-base-content/50"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
									/>
								</svg>
							</div>
						)}
						{isPopular && (
							<div className="absolute top-2 right-2 z-10">
								<span className="px-3 py-1.5 text-xs font-bold text-white uppercase rounded-full bg-primary shadow-lg flex items-center gap-1.5 border border-primary/20 hover:bg-primary/90 transition-all duration-300 group/popular">
									<svg
										xmlns="http://www.w3.org/2000/svg"
										className="h-4 w-4 animate-pulse"
										viewBox="0 0 20 20"
										fill="currentColor"
									>
										<path
											fillRule="evenodd"
											d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 5a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1V8a1 1 0 011-1zm7-5a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0V6h-1a1 1 0 110-2h1V3a1 1 0 011-1zm-7 7a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zm7 0a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1v-1a1 1 0 011-1z"
											clipRule="evenodd"
										/>
									</svg>
									<span>Popular</span>
									{productCategory && (
										<span className="absolute -bottom-8 left-1/2 -translate-x-1/2 px-2 py-1 text-xs font-medium text-white uppercase rounded-full bg-primary/90 shadow-md opacity-0 group-hover/popular:opacity-100 transition-opacity duration-200 whitespace-nowrap">
											{productCategory.name}
										</span>
									)}
								</span>
							</div>
						)}
						{hasDiscount && discountPercentage > 0 && (
							<div className={`absolute top-2 left-2`}>
								<span className="px-2 py-1 text-xs font-bold text-white uppercase rounded-full bg-error shadow-md">
									{discountPercentage}% OFF
								</span>
							</div>
						)}
						{productCategory && !isPopular && (
							<div className="absolute top-2 right-2">
								<span className="px-2 py-1 text-xs font-medium text-white uppercase rounded-full bg-primary/90 shadow-md">
									{productCategory.name}
								</span>
							</div>
						)}
						{isOutOfStock && (
							<div className="absolute inset-0 flex items-center justify-center">
								<div className="absolute inset-0 bg-gradient-to-b from-black/70 to-black/40"></div>
								<div className="relative flex flex-col items-center justify-center p-4 text-center">
									<div className="mb-3">
										<svg
											xmlns="http://www.w3.org/2000/svg"
											viewBox="0 0 24 24"
											fill="currentColor"
											className="w-10 h-10 text-white"
										>
											<path
												fillRule="evenodd"
												d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM12.75 6a.75.75 0 00-1.5 0v6c0 .414.336.75.75.75h4.5a.75.75 0 000-1.5h-3.75V6z"
												clipRule="evenodd"
											/>
										</svg>
									</div>
									<div className="px-3 py-1 mb-2 text-sm font-medium text-white uppercase bg-error/80 rounded-full shadow-md">
										Out of Stock
									</div>
								</div>
							</div>
						)}
					</div>
					<div className="p-5">
						<div className="flex items-center justify-between mb-2">
							<h3
								className="text-lg font-semibold line-clamp-1 text-base-content cursor-pointer"
								title={name}
							>
								{name}
							</h3>
							<div className="flex flex-col items-end">
								{hasDiscount && discountedPrice ? (
									<>
										<span className="font-bold text-error">
											{isBulk ? (
												<>
													{formatCurrency(
														(discountedPrice || price) * (bulkQuantity || 1)
													)}
												</>
											) : (
												formatCurrency(discountedPrice)
											)}
										</span>
										<span className="text-sm text-base-content/60 line-through">
											{isBulk ? (
												<>
													{formatCurrency(
														(price || price) * (bulkQuantity || 1)
													)}
												</>
											) : (
												formatCurrency(price)
											)}
										</span>
									</>
								) : (
									<span className="font-bold text-primary">
										{isBulk ? (
											<>
												{formatCurrency(
													(displayPrice || price) * (bulkQuantity || 1)
												)}
											</>
										) : (
											formatCurrency(price)
										)}
									</span>
								)}
							</div>
						</div>
						<p className="text-sm line-clamp-2 text-base-content/70">
							{shortDescription.length > 100
								? shortDescription.slice(0, 100) + "..."
								: shortDescription}{" "}
						</p>
					</div>
				</Link>
				<div className="px-5 pb-5 mt-auto">
					<div className="flex items-center justify-between">
						{product.isInstantDelivery ? (
							<span className="flex items-center text-xs text-success">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									strokeWidth={2.5}
									stroke="currentColor"
									className="w-5 h-5 mr-1.5"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
								Instant Delivery
							</span>
						) : null}
						<div className="flex-shrink-0 ml-auto">
							<Link
								href={`/products/${slug}`}
								className="inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium text-white whitespace-nowrap transition-all duration-300 rounded-xl bg-primary hover:bg-primary/90 hover:shadow-lg"
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									strokeWidth={1.5}
									stroke="currentColor"
									className="w-4 h-4 mr-1.5"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
									/>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
									/>
								</svg>
								View Details
							</Link>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ProductCard;
